const AUREN_QUICK_ADD_SELECTOR = '.quick-add__button--choose';

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const formatMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'SEK';
  const locale = document.documentElement.lang || 'sv-SE';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
};

const productUrlFromButton = (button) => {
  const card = button.closest('product-card');
  const link = card?.querySelector('a[href*="/products/"]');
  return link?.href || '';
};

const productJsonUrl = (productUrl) => {
  const url = new URL(productUrl, window.location.origin);
  return `${url.origin}${url.pathname}.js`;
};

const currentVariantId = (productUrl) => {
  const url = new URL(productUrl, window.location.origin);
  return url.searchParams.get('variant');
};

const imageForVariant = (variant, product) => {
  return variant?.featured_image?.src || product?.featured_image || product?.images?.[0] || '';
};

const uniqueOptionValues = (product, optionIndex) => {
  const values = [];
  for (const variant of product.variants || []) {
    const value = variant.options?.[optionIndex];
    if (value && !values.includes(value)) values.push(value);
  }
  return values;
};

const updateCartIndicators = async () => {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();
    const selectors = [
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble',
      '[data-cart-count]',
      '[data-testid="cart-count"]',
    ];
    for (const selector of selectors) {
      document.querySelectorAll(selector).forEach((element) => {
        if (element instanceof HTMLElement) element.textContent = String(cart.item_count);
      });
    }
  } catch (_) {
    // The cart was updated even if a visual counter cannot be found.
  }
};

const ensureStyles = () => {
  if (document.getElementById('auren-variant-quick-add-styles')) return;
  const style = document.createElement('style');
  style.id = 'auren-variant-quick-add-styles';
  style.textContent = `
    .auren-quick-add { width: min(92vw, 780px); padding: 26px; }
    .auren-quick-add__loading { min-height: 180px; display: grid; place-items: center; }
    .auren-quick-add__grid { display: grid; grid-template-columns: minmax(240px, 1fr) minmax(240px, 1fr); gap: 24px; align-items: start; }
    .auren-quick-add__image { width: 100%; aspect-ratio: 3 / 4; object-fit: cover; background: rgba(0,0,0,.04); }
    .auren-quick-add__title { margin: 0 0 8px; font-size: 1.45rem; line-height: 1.2; }
    .auren-quick-add__price { margin: 0 0 22px; font-size: 1.05rem; }
    .auren-quick-add__field { display: grid; gap: 7px; margin-bottom: 16px; }
    .auren-quick-add__field label { font-weight: 600; }
    .auren-quick-add__select { width: 100%; min-height: 46px; padding: 10px 38px 10px 12px; border: 1px solid currentColor; border-radius: 2px; background: transparent; color: inherit; font: inherit; }
    .auren-quick-add__status { min-height: 22px; margin: 4px 0 14px; font-size: .92rem; }
    .auren-quick-add__button { width: 100%; min-height: 48px; }
    .auren-quick-add__error { color: #9b1c1c; }
    .auren-quick-add__success { color: #276238; }
    @media (max-width: 749px) {
      .auren-quick-add { width: 100%; padding: 18px; }
      .auren-quick-add__grid { grid-template-columns: 140px minmax(0, 1fr); gap: 16px; }
      .auren-quick-add__title { font-size: 1.15rem; }
    }
    @media (max-width: 430px) {
      .auren-quick-add__grid { grid-template-columns: 128px minmax(0, 1fr); gap: 14px; }
    }
  `;
  document.head.appendChild(style);
};

const openDialog = () => {
  const component = document.getElementById('quick-add-dialog');
  if (!component) return null;
  if (typeof component.showDialog === 'function') component.showDialog();
  else component.querySelector('dialog')?.showModal();
  return component;
};

const closeDialog = () => {
  const component = document.getElementById('quick-add-dialog');
  if (!component) return;
  if (typeof component.closeDialog === 'function') component.closeDialog();
  else component.querySelector('dialog')?.close();
};

const renderQuickAdd = (content, product, productUrl) => {
  const requestedId = currentVariantId(productUrl);
  let selectedVariant = product.variants.find((variant) => String(variant.id) === String(requestedId));
  if (!selectedVariant?.available) selectedVariant = product.variants.find((variant) => variant.available) || product.variants[0];

  const optionNames = Array.isArray(product.options) ? product.options : [];
  const selections = selectedVariant?.options ? [...selectedVariant.options] : [];

  const fields = optionNames.map((optionName, index) => {
    const name = typeof optionName === 'string' ? optionName : optionName?.name || `Val ${index + 1}`;
    const values = uniqueOptionValues(product, index);
    return `
      <div class="auren-quick-add__field">
        <label for="auren-quick-add-option-${index}">${escapeHtml(name)}</label>
        <select class="auren-quick-add__select" id="auren-quick-add-option-${index}" data-option-index="${index}">
          ${values.map((value) => `<option value="${escapeHtml(value)}" ${value === selections[index] ? 'selected' : ''}>${escapeHtml(value)}</option>`).join('')}
        </select>
      </div>`;
  }).join('');

  content.innerHTML = `
    <div class="auren-quick-add">
      <div class="auren-quick-add__grid">
        <img class="auren-quick-add__image" src="${escapeHtml(imageForVariant(selectedVariant, product))}" alt="${escapeHtml(product.title)}">
        <div>
          <h2 class="auren-quick-add__title">${escapeHtml(product.title)}</h2>
          <p class="auren-quick-add__price">${formatMoney(selectedVariant?.price)}</p>
          <form class="auren-quick-add__form">
            ${fields}
            <div class="auren-quick-add__status" aria-live="polite"></div>
            <button type="submit" class="button auren-quick-add__button" ${selectedVariant?.available ? '' : 'disabled'}>
              ${selectedVariant?.available ? 'Lägg i varukorgen' : 'Slutsåld'}
            </button>
          </form>
        </div>
      </div>
    </div>`;

  const form = content.querySelector('.auren-quick-add__form');
  const price = content.querySelector('.auren-quick-add__price');
  const image = content.querySelector('.auren-quick-add__image');
  const status = content.querySelector('.auren-quick-add__status');
  const submit = content.querySelector('.auren-quick-add__button');
  const selects = [...content.querySelectorAll('.auren-quick-add__select')];

  const resolveVariant = () => {
    const chosen = selects.map((select) => select.value);
    selectedVariant = product.variants.find((variant) =>
      variant.options?.every((value, index) => value === chosen[index])
    );

    if (price) price.textContent = selectedVariant ? formatMoney(selectedVariant.price) : '';
    if (image && selectedVariant) image.src = imageForVariant(selectedVariant, product);
    if (status) {
      status.textContent = selectedVariant && !selectedVariant.available ? 'Den valda varianten är slutsåld.' : '';
      status.className = 'auren-quick-add__status';
    }
    if (submit) {
      submit.disabled = !selectedVariant?.available;
      submit.textContent = selectedVariant?.available ? 'Lägg i varukorgen' : 'Slutsåld';
    }
  };

  selects.forEach((select) => select.addEventListener('change', resolveVariant));

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    resolveVariant();
    if (!selectedVariant?.available || !submit) return;

    submit.disabled = true;
    submit.textContent = 'Lägger till…';
    if (status) status.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: selectedVariant.id, quantity: 1 }] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.description || result?.message || 'Produkten kunde inte läggas till.');

      await updateCartIndicators();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: String(selectedVariant.id),
          data: { didError: false, source: 'auren-quick-add', variantId: String(selectedVariant.id) },
        },
      }));

      if (status) {
        status.textContent = 'Tillagd i varukorgen.';
        status.className = 'auren-quick-add__status auren-quick-add__success';
      }
      submit.textContent = 'Tillagd';
      window.setTimeout(closeDialog, 700);
    } catch (error) {
      if (status) {
        status.textContent = error instanceof Error ? error.message : 'Något gick fel. Försök igen.';
        status.className = 'auren-quick-add__status auren-quick-add__error';
      }
      submit.disabled = false;
      submit.textContent = 'Lägg i varukorgen';
    }
  });
};

const handleQuickAdd = async (button) => {
  ensureStyles();
  const productUrl = productUrlFromButton(button);
  const content = document.getElementById('quick-add-modal-content');
  if (!productUrl || !content) return;

  openDialog();
  content.innerHTML = '<div class="auren-quick-add auren-quick-add__loading">Laddar alternativ…</div>';

  try {
    const response = await fetch(productJsonUrl(productUrl), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const product = await response.json();
    renderQuickAdd(content, product, productUrl);
  } catch (_) {
    content.innerHTML = `
      <div class="auren-quick-add">
        <p class="auren-quick-add__error">Alternativen kunde inte laddas.</p>
        <a class="button" href="${escapeHtml(productUrl)}">Öppna produkten</a>
      </div>`;
  }
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest(AUREN_QUICK_ADD_SELECTOR) : null;
  if (!(target instanceof HTMLButtonElement)) return;
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  handleQuickAdd(target);
}, true);
